var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { getInitConfig } from 'ziphy-web-shared';
import { showAlert } from 'ziphy-web-shared/basic/lib/utilities';
import { useLivequeueWs } from 'ziphy-web-shared/basic/utils/useLivequeueWS';
import $livequeueChats from '@core/src/store/livequeueChatStore';
export const useChatMessages = (chatId) => {
    const chatContainerRef = useRef(null);
    const topMessageRef = useRef(null);
    const bottomMessageRef = useRef(null);
    const isLoadingMore = useRef(false);
    const [isLoading, setIsLoading] = useState(false);
    const { t } = useTranslation();
    const wsOptions = useMemo(() => ({
        url: `${getInitConfig().apiService.urls.livequeueUrl}ws`,
        onMessage: (msg) => {
            var _a;
            if (((_a = msg.payload) === null || _a === void 0 ? void 0 : _a.chat_id) === chatId) {
                $livequeueChats.addMessage(msg.payload);
            }
        },
        onOpen: () => console.log(`[WS connected] chatId=${chatId}`),
        onClose: () => console.log(`[WS disconnected] chatId=${chatId}`),
        onError: (err) => {
            showAlert.error(t('ws.error.main_error'));
        },
    }), [chatId]);
    const { connected, disconnect } = useLivequeueWs(wsOptions);
    const evaluateScrollPosition = useCallback(() => {
        const container = chatContainerRef.current;
        if (!container)
            return;
        const threshold = 4;
        const distanceFromBottom = container.scrollHeight - container.scrollTop - container.clientHeight;
        const isAtBottomRaw = distanceFromBottom <= threshold;
        const isAtBottom = isAtBottomRaw &&
            (!$livequeueChats.unreadAnchorId || $livequeueChats.unreadAnchorVisible === false);
        $livequeueChats.setScrollState(isAtBottom);
        if (isAtBottom) {
            const latest = [...$livequeueChats.currentChatMessages]
                .reverse()
                .find((m) => !m._id.includes(':'));
            if (latest) {
                const sub = $livequeueChats.getSubscriptionForChat(chatId);
                if ((sub === null || sub === void 0 ? void 0 : sub.last_viewed_message_id) !== latest._id) {
                    $livequeueChats.markMessageAsRead(chatId, latest._id);
                }
            }
        }
    }, [chatId]);
    const scrollToBottom = useCallback(() => {
        const container = chatContainerRef.current;
        if (!container)
            return;
        requestAnimationFrame(() => {
            container.scrollTop = container.scrollHeight;
            evaluateScrollPosition();
        });
    }, [evaluateScrollPosition]);
    const handleScroll = useCallback(() => {
        evaluateScrollPosition();
    }, [evaluateScrollPosition]);
    useEffect(() => {
        return () => {
            disconnect();
        };
    }, [chatId, disconnect]);
    useEffect(() => {
        let cancelled = false;
        const init = () => __awaiter(void 0, void 0, void 0, function* () {
            isLoadingMore.current = true;
            setIsLoading(true);
            try {
                yield $livequeueChats.initChat(chatId);
            }
            finally {
                if (!cancelled) {
                    isLoadingMore.current = false;
                    setIsLoading(false);
                    scrollToBottom();
                }
            }
        });
        init();
        return () => {
            cancelled = true;
        };
    }, [chatId, scrollToBottom]);
    const handleLoadMore = useCallback(() => __awaiter(void 0, void 0, void 0, function* () {
        if (isLoadingMore.current)
            return;
        if (!$livequeueChats.hasMoreBefore)
            return;
        const container = chatContainerRef.current;
        const firstMsg = $livequeueChats.currentChatMessages[0];
        if (!container || !firstMsg)
            return;
        const prevScrollHeight = container.scrollHeight;
        const prevTopMessageId = firstMsg._id;
        isLoadingMore.current = true;
        setIsLoading(true);
        yield $livequeueChats.getChatMessages({
            chat_id: chatId,
            limit: 20,
            before_message_id: prevTopMessageId,
        }, { prepend: true, direction: 'before' });
        requestAnimationFrame(() => {
            const newScrollHeight = container.scrollHeight;
            container.scrollTop += newScrollHeight - prevScrollHeight;
            isLoadingMore.current = false;
            setIsLoading(false);
            evaluateScrollPosition();
        });
    }), [chatId, evaluateScrollPosition]);
    useEffect(() => {
        const topEl = topMessageRef.current;
        const container = chatContainerRef.current;
        if (!topEl || !container)
            return;
        const observer = new IntersectionObserver((entries) => {
            const [entry] = entries;
            if (entry.isIntersecting)
                handleLoadMore();
        }, { root: container, threshold: 0.1 });
        observer.observe(topEl);
        return () => observer.disconnect();
    }, [handleLoadMore]);
    const handleLoadNewer = useCallback(() => __awaiter(void 0, void 0, void 0, function* () {
        if (isLoadingMore.current)
            return;
        if (!$livequeueChats.hasMoreAfter)
            return;
        const container = chatContainerRef.current;
        const lastRealMessage = [...$livequeueChats.currentChatMessages]
            .reverse()
            .find((m) => !m._id.includes(':'));
        if (!container || !lastRealMessage)
            return;
        // Preserve distance from bottom so scroll position remains the same after appending
        const prevBottomOffset = container.scrollHeight - container.scrollTop - container.clientHeight;
        isLoadingMore.current = true;
        setIsLoading(true);
        yield $livequeueChats.getChatMessages({
            chat_id: chatId,
            limit: 20,
            after_message_id: lastRealMessage._id,
        }, { append: true, direction: 'after' });
        // Restore the same bottom offset (keeps user at bottom if they were there)
        requestAnimationFrame(() => {
            if (chatContainerRef.current) {
                const c = chatContainerRef.current;
                c.scrollTop = c.scrollHeight - c.clientHeight - prevBottomOffset;
                evaluateScrollPosition();
            }
        });
        isLoadingMore.current = false;
        setIsLoading(false);
    }), [chatId, evaluateScrollPosition]);
    useEffect(() => {
        const bottomEl = bottomMessageRef.current;
        const container = chatContainerRef.current;
        if (!bottomEl || !container)
            return;
        const observer = new IntersectionObserver((entries) => {
            const [entry] = entries;
            if (entry.isIntersecting)
                handleLoadNewer();
        }, { root: container, threshold: 0.1 });
        observer.observe(bottomEl);
        return () => observer.disconnect();
    }, [handleLoadNewer]);
    useEffect(() => {
        if ($livequeueChats.pendingAutoScroll) {
            scrollToBottom();
        }
    }, [$livequeueChats.pendingAutoScroll, scrollToBottom]);
    useEffect(() => {
        evaluateScrollPosition();
    }, [$livequeueChats.currentChatMessages.length, evaluateScrollPosition]);
    return {
        chatContainerRef,
        topMessageRef,
        bottomMessageRef,
        isLoadingMore: isLoading,
        connected,
        handleScroll,
        scrollToBottom,
    };
};
